/**
 * Die Umformungen, die die Oberflaeche zwischen dem Abruf einer Quelle des Gesamtbestands und dem Zustand vornimmt - als reine
 * Funktionen. Bis zum KI-Zugang (Stufe 1) standen sie inline in `state/Speicher.tsx#bestandLadenEinzeln` (Spalten der
 * Board-Konfiguration, Kennungen der zu ladenden Lieferanten) und in `components/App/zeitstrahlSendungen.ts` (Auswahl der Bestellungen
 * fuer den Sammelabruf, Abbildung der Treffer auf die Sendung des Zeitstrahls). Der Server (`backend/src/ki/auswertung/frontend/`)
 * muss genau dieselben Schritte fahren, sonst weichen seine Zeilen und Summen von denen der Oberflaeche ab; deshalb ist EINE Fassung
 * hier, die Oberflaeche ruft sie, und die zeichengleiche Kopie liegt auf dem Server (`npm run gen:ki-auswertung --workspace backend`).
 *
 * Dazu gehoert auch die Vorgabe, WELCHE Zustaende (aktiv, archiviert, geloescht) die Oberflaeche beim Erstladen anfordert, soweit sie
 * von der Rolle abhaengt (`vorgabeZustaende`): ein rechnendes Werkzeug ohne Archivangabe soll dieselbe Menge sehen wie die Oberflaeche.
 *
 * Die Abrufe selbst (`fetch`) bleiben in `api/`; hier steht nur, was danach mit der Antwort geschieht. Was die Oberflaeche an einer
 * Quelle NICHT umformt (Deals, Kurssicherungsuebersicht: nur `JSON.parse`), steht nicht hier - dort ist Drahtform = Zustand.
 *
 * Regel fuer dieses Modul (`test/lib/reineKette.test.ts`): nur `.ts`-Importe innerhalb von `frontend/src`, kein `react`, kein
 * `import.meta`, keine Browser-Globals.
 */
import { bestellungenZusammensetzen } from "./bestandZusammensetzen.ts";
import { istAbgeleiteteKennung } from "./jtlZusammenfuehren.ts";
import type { JtlBestellungRoh, JtlLoeschFelder } from "./jtlZusammenfuehren.ts";
import { activeReportingOrders } from "./status.ts";
import type { BoardColumnLike, StatusDefinitionen } from "./status.ts";
import type { Order, Sendung, SendungsTreffer } from "./types.ts";

/**
 * Die Spalten des Boards aus dem Konfigurationswert `boardColumns` (`GET /api/config/boardColumns`): sein Feld `value`, wenn es eine
 * Liste ist, sonst eine leere Liste. `null` (unbekannter Schluessel, 404) und jeder andere Wert ergeben ebenfalls eine leere Liste.
 */
export function boardSpaltenAusKonfiguration(wert: { value?: unknown } | null | undefined): BoardColumnLike[] {
  const spalten = wert?.value;
  return (Array.isArray(spalten) ? spalten : []) as BoardColumnLike[];
}

/**
 * Die ganze Folge, die `state/Speicher.tsx#bestandLadenEinzeln` (Zweig 2) zwischen den Rohquellen und dem Zustand faehrt: aus dem
 * Konfigurationswert die Board-Spalten, daraus mit den JTL-Zeilen und den Hub-Zeilen die Bestellungen (zusammenfuehren, dann den
 * Status gegen die Spalten normalisieren). Die Oberflaeche und der Server des KI-Zugangs rufen genau diese eine Funktion; zwischen
 * Ergebnis und Zustand folgt in `Speicher.tsx` kein weiterer Schritt (`backend/test/ki-bestand-quelltext.test.ts`
 * sichert das am Quelltext zu).
 *
 * `hubZeilen` sind die Zeilen im Zustand der Oberflaeche (`bestellungenLaden()`), `jtlRoh` die Zeilen von `GET /api/jtl/orders`,
 * `spaltenWert` die Antwort von `GET /api/config/boardColumns` (oder `null`).
 */
export function bestellungenAusQuellen(
  jtlRoh: readonly JtlBestellungRoh[],
  hubZeilen: readonly Order[],
  spaltenWert: { value?: unknown } | null | undefined,
): { bestellungen: Array<Order & JtlLoeschFelder>; boardSpalten: BoardColumnLike[] } {
  const boardSpalten = boardSpaltenAusKonfiguration(spaltenWert);
  return { bestellungen: bestellungenZusammensetzen(jtlRoh, hubZeilen, boardSpalten), boardSpalten };
}

/**
 * Die Rolle, die immer den vollstaendigen Bestand sieht (aktive UND archivierte): die Zahlungsansicht haelt archivierte Bestellungen
 * mit offener Zahlungsaufgabe absichtlich sichtbar (Auftrag "Serverseitiger Archivfilter + Ladeanzeige", Befund 3).
 */
export function istZahlungsrolle(rolle: string | undefined): boolean {
  return rolle === "payment_manager";
}

/** Die Zustaende, die ein Bestandsabruf anfordern kann (`state/adresse.ts#BoardState`; dort nicht importierbar, die Kette bliebe nicht rein). */
type Zustand = "active" | "archived" | "deleted";

/**
 * Die Zustandsmenge, mit der die Oberflaeche des Nutzers ohne Angabe in der Adresse laedt: die Zahlungsrolle `["active", "archived"]`
 * (OHNE `"deleted"`: geloeschte Bestellungen waren in dieser Ausnahme nie gemeint), jede andere Rolle `["active"]` (die Vorgabe der
 * Listenroute ohne `state`-Parameter). Jeder Aufruf liefert ein neues Feld. `state/Speicher.tsx#ersteZustandswahl` nutzt sie fuer den
 * Rollenzweig, die rechnenden Werkzeuge des KI-Zugangs (`backend/src/ki/auswertung/bestand.ts#archivwahlFuer`) fuer ihre Vorgabe.
 */
export function vorgabeZustaende(rolle: string | undefined): readonly Zustand[] {
  return istZahlungsrolle(rolle) ? ["active", "archived"] : ["active"];
}

/**
 * Die Lieferanten, die zu den Bestellungen geladen werden (`GET /api/suppliers/:kLieferant` je Kennung): die `supplierId` jeder
 * Bestellung, je Kennung einmal, in der Reihenfolge des ersten Auftretens; Bestellungen ohne Lieferant bleiben draussen.
 */
export function lieferantenKennungen(bestellungen: readonly Order[]): string[] {
  return [...new Set(bestellungen.map((o) => o.supplierId).filter(Boolean))] as string[];
}

/**
 * Fuer welche Bestellungen die Zeitstrahl-Liste ihre Sendung abruft (`POST /api/orders/shipments`): die sichtbaren, also nicht
 * archivierten; abgeleitete JTL-Kennungen (`jtl-<Nummer>`) haben keine Hub-Verknuepfung und bleiben draussen (BE-10245, 25.08.2026).
 */
export function zeitstrahlBestellkennungen(
  bestellungen: readonly Order[],
  definitionen: StatusDefinitionen,
): string[] {
  return activeReportingOrders(bestellungen, definitionen)
    .map((o) => String(o.id))
    .filter((id) => !istAbgeleiteteKennung(id));
}

/** Alles mit einer ETA: ein Treffer mit `eta` oder eine Zeile mit offenen Feldern (`Order`, `Sendung` tragen ihre Felder ueber einen Index). */
type MitEta = { eta?: unknown } | Readonly<Record<string, unknown>>;

/**
 * Die ETA einer Zeile des Zeitstrahls und woher sie kommt: die der Sendung, sonst die der Bestellung (`Zeitstrahlseite`: sie sortiert und
 * zeigt damit). Eine leere Sendungs-ETA verdeckt die der Bestellung; ohne Wert ist `eta` `null`. Die EINE Regel: die Zeitstrahlseite und die
 * KI-Werkzeuge (`sendung_status`, `anlieferungen_artikel`, ueber die Kopie auf dem Server) rufen sie.
 */
export function etaMitQuelle(
  sendung: MitEta | null | undefined,
  order: MitEta | null | undefined,
): { eta: string | null; etaQuelle: "sendung" | "bestellung" | null } {
  const ausSendung = sendung?.eta ?? null;
  const roh = ausSendung ?? order?.eta ?? null;
  if (!roh) return { eta: null, etaQuelle: null };
  return { eta: String(roh), etaQuelle: ausSendung !== null ? "sendung" : "bestellung" };
}

/** Sammelrouten-Treffer fuer die Stationsspur; fehlende Stationen bleiben defensiv leer. */
export function sendungAusTreffer(treffer: SendungsTreffer): Sendung {
  return {
    id: treffer.id,
    trackingNo: treffer.dsvShipmentNo,
    etd: treffer.etd,
    eta: treffer.eta,
    stationen: treffer.stationen ?? [],
  };
}

/**
 * Die Antwort der Sammelroute (je Bestellung ein Treffer oder `null`) als Sendungen des Zeitstrahls. `null` bleibt `null`:
 * "nicht verknuepft" ist etwas anderes als "noch nicht geladen" (fehlender Schluessel).
 */
export function sendungenAbbilden(
  geladen: Readonly<Record<string, SendungsTreffer | null>>,
): Record<string, Sendung | null> {
  const abgebildet: Record<string, Sendung | null> = {};
  for (const [orderId, treffer] of Object.entries(geladen)) {
    abgebildet[orderId] = treffer ? sendungAusTreffer(treffer) : null;
  }
  return abgebildet;
}
